import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnInit,
  Output,
  SecurityContext,
  PLATFORM_ID,
  OnDestroy,
  RESPONSE_INIT,
  inject,
  DOCUMENT,
  ChangeDetectionStrategy,
} from '@angular/core';

import { EnvToken, SeoSegmentsToken } from '@app/core/env.tokens';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

import { GuideService } from '@services/http/guide.service';
import { SEOService } from '@services/logic/seo.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { setBreadCrumb } from '@utils/setBreadCrumb';
import { ScrollProgressBarDirective } from '@app/directives/scroll-progress-bar-directive';

import { BreadcrumbListComponent } from '@components/shared/breadcrumb-list/breadcrumb-list.component';
import { FooterComponent } from '@components/shared/footer/footer.component';
import { RelatedGuidesComponent } from '@components/seoPageElements/relatedGuides/relatedGuides.component';

import { JsonLdPipe } from '@app/pipes/json-ld-pipe';
import { TranslatePipe } from '@app/pipes/translate.pipe';

import { HowTo } from '@models/guides/howToGuide.model';
import { ExternalService } from '@app/services/http/external.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SafePipe } from '@app/pipes/safe';
import { HappyHelperBannerComponent } from '@components/seoPageElements/happy-helper-banner/happy-helper-banner.component';
import { HappyHelperReferenceComponent } from '@components/seoPageElements/happy-helper-reference/happy-helper-reference.component';
import { GenericButtonWithArrow } from '@components/shared/genericButtonWithArrow/genericButtonWithArrow';
import { HappyHelperUspsComponent } from '@components/seoPageElements/happy-helper-usps/happy-helper-usps.component';
import { ServerSideResponseService } from '@services/logic/serverSideResponse.service';

@Component({
  imports: [
    CommonModule,
    BreadcrumbListComponent,
    MatIconModule,
    TranslatePipe,
    RelatedGuidesComponent,
    FooterComponent,
    JsonLdPipe,
    ScrollProgressBarDirective,
    RouterLink,
    SafePipe,
    NgOptimizedImage,
    HappyHelperBannerComponent,
    HappyHelperReferenceComponent,
    HappyHelperUspsComponent,
    GenericButtonWithArrow,
  ],
  providers: [SEOService, JsonLdPipe, TranslatePipe, ExternalService],
  selector: 'app-how-to',
  templateUrl: './guide.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../priceGuides/priceGuideCss.scss'],
})
export class GuideComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly env = inject(EnvToken);
  protected readonly seg = inject(SeoSegmentsToken);
  private readonly cdr = inject(ChangeDetectorRef);

  @HostListener('window:resize')
  @Output()
  howTo!: HowTo;
  mainCat!: string;
  slug!: string;
  subCategory?: string;
  isBrowser: boolean = false;
  videoLoaded: boolean = false;
  innerWidth: number = 0;
  youtubeSchema: any;
  answer: string = 'Svar:';
  private destroy$ = new Subject<void>();
  private readonly responseInit = inject(RESPONSE_INIT, { optional: true });
  // Add the content property to store the processed HTML
  content: string = '';

  // Table of content
  h2Texts: { id: string; text: string }[] = []; // Here's the change
  partsOfUrl!: { name: string; url: string }[];
  tableOfContentExpanded = false;

  howToSchema: any;
  dangerousVideoUrl?: string;
  youTubeVidLink?: SafeResourceUrl;
  youtubeVidTitle?: string;

  private readonly cleaningCategories = ['rengoering', 'rengøring'];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private activatedRoute: ActivatedRoute,
    private guideService: GuideService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private seoService: SEOService,
    public externalService: ExternalService,
    private serverSideResponseService: ServerSideResponseService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
    // Check for duplicate parameters and redirect if found
    this.checkAndRedirectForDuplicates();

    this.fetchHowToGuide();
    if (this.isBrowser) {
      this.innerWidth = window.innerWidth;
      if (innerWidth > 990) {
        this.tableOfContentExpanded = true;
      }
    }
  }

  ngAfterViewInit() {
    if (this.howTo) {
      this.updateVideoUrl(this.howTo?.content.youtube_embed_id);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Method to fetch a how-to guide and set up the page accordingly
  fetchHowToGuide() {
    // Subscribe to the paramMap observable of the activated route
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.slug = params.get('guideSlug') ?? '';
        this.mainCat = params.get('headCategory') ?? '';
        this.subCategory = params.get('subCategory') ?? '';
        this.guideService
          .fetchGuideBySlug(this.slug, this.mainCat, this.subCategory)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              if (!res && this.responseInit) {
                this.responseInit.status = 404;
                return;
              }
              this.howTo = res;
              if (this.howTo?.content?.rich_text && this.isBrowser) {
                try {
                  const originalContent = this.howTo.content.rich_text;
                  const modifiedHtmlString = this.addIDsToH2s(originalContent);
                  this.content = modifiedHtmlString;
                  this.h2Texts = this.getH2FromHtmlString(modifiedHtmlString);
                } catch (error) {}
              }
              // Set the YouTube video title if it's available
              if (this.howTo?.content.youtube_title) {
                this.youtubeVidTitle = this.capitalizeFirstLetter(
                  this.howTo?.content.youtube_title,
                );
              }
              if (this.howTo) {
                this.updateVideoUrl(this.howTo?.content.youtube_embed_id);
              }
              this.setMetaDetails();
              if (this.isBrowser) {
                // Inject the YouTube schema if it's available
                if (this.howTo?.content.youtube_schema) {
                  this.injectYoutubeSchema(this.howTo?.content.youtube_schema);
                }
              }
              this.cdr.markForCheck();
            },
            error: (err) => {
              // 301 Permanent redirects handle correct by response status code!
              if (err.status === 200 && err.url && this.isBrowser) {
                // Refresh the component with the new URL
                return (window.location.href = err.url);
              }

              if (err.status === 301) {
                // Check if redirectTo attribute exists in the error object
                if (err.error && err.error.redirectTo) {
                  // Extract the URL from the redirectTo attribute and navigate to it
                  const redirectUrl = new URL(err.error.redirectTo).pathname;
                  return this.router.navigate([redirectUrl]);
                }

                if (err.error && err.error.url) {
                  return this.router.navigate([err.error.url]);
                }
              }

              if (err.status === 404) {
                // Set 404 status code for server response during SSR
                this.serverSideResponseService.setNotFound();
                // skipLocationChange: keep the requested URL so the SSR engine
                // renders the error page with the 404 status instead of
                // attempting an invalid non-3xx redirect.
                return this.router.navigate(['404'], {
                  skipLocationChange: true,
                });
              }

              return err;
            },
          });
      });
  }

  /**
   * The one URL this guide should be indexed under, rebuilt from the document's
   * own category rather than from the route it was reached by.
   *
   * `buildMainGuideRoutes` registers guide routes at three nesting depths and
   * resolves the document from `:guideSlug` alone, so ancestor segments are
   * decorative: `/have/haek/klipning-af-haek/klip-liguster/guide`,
   * `/have/haek/klip-liguster/guide` and `/haek/klip-liguster/guide` all serve
   * this page. Deriving the canonical from `router.url` therefore made every
   * variant self-canonical and told Google they were distinct pages.
   *
   * Mirrors `urlCategoryHelper` (the sitemap's URL builder) exactly, including
   * its deliberate omission of `subSubCategory`, so page and sitemap agree.
   * Same shape as `PriceGuideComponent.canonicalPath()`, which already did this.
   */
  public canonicalPath(): string {
    const segments = [
      this.howTo?.mainCategory?.slug,
      this.howTo?.subCategory?.slug,
      this.howTo?.newSlug || this.howTo?.slug,
    ].filter(Boolean) as string[];
    const deduped = segments.filter(
      (segment, i) => segment !== segments[i + 1],
    );
    return `/${deduped.join('/')}/${this.seg.suffix.guide}`;
  }

  /**
   * Removes duplicate consecutive parameters from URL path
   * Example: /rengoering/rengoering/guide becomes /rengoering/guide
   * @param urlPath - The URL path to clean
   * @returns Cleaned URL path with duplicates removed
   */
  private removeDuplicateParams(urlPath: string): string {
    if (!urlPath) return urlPath;

    const segments = urlPath.split('/').filter((segment) => segment.length > 0);
    const cleanedSegments: string[] = [];

    for (let i = 0; i < segments.length; i++) {
      const currentSegment = segments[i];
      const nextSegment = segments[i + 1];

      // Only add current segment if it's not the same as the next one
      if (currentSegment !== nextSegment) {
        cleanedSegments.push(currentSegment);
      }
    }

    return '/' + cleanedSegments.join('/');
  }

  /**
   * Check for duplicate parameters in URL and redirect if found
   */
  private checkAndRedirectForDuplicates(): void {
    if (!this.isBrowser) return;

    const currentUrl = this.router.url;
    const cleanedUrl = this.removeDuplicateParams(currentUrl);

    // If the cleaned URL is different from current URL, redirect
    if (cleanedUrl !== currentUrl) {
      const fullCleanedUrl = `${this.env.domain}${cleanedUrl}`;
      // Use 303 redirect (See Other) for SEO-friendly redirect
      window.location.replace(fullCleanedUrl);
    }
  }

  private setBreadCrumb() {
    this.partsOfUrl = setBreadCrumb({
      entity: this.howTo,
      hubIndex: this.seg.suffix.hubIndex,
      url: this.router.url,
    });
  }

  private setMetaDetails() {
    this.setBreadCrumb();

    // Built from the document, never from `router.url` -- see canonicalPath().
    const canonicalUrl = `${this.env.domain}${this.canonicalPath()}`;

    this.seoService.setPageMeta({
      // No page-type token: " | guide" spent 8 of the 60 rendered title chars
      // on a word that earns no clicks, pushing 88.5% of guide titles past the
      // SERP budget (2026-08 audit). setPageMeta still appends " - Handyhand".
      metaTitle: this.howTo.title_tag || '',
      metaDesc: this.howTo.meta_description!,
      img: this.howTo.imgUrl,
      canonicalUrl: canonicalUrl,
    });
  }

  private getH2FromHtmlString(
    htmlString: string,
  ): { id: string; text: string }[] {
    if (this.isBrowser) {
      const div = this.document.createElement('div');
      div.innerHTML = htmlString;
      const h2Elements = div.getElementsByTagName('h2');
      return Array.from(h2Elements).map((h2) => ({
        id: h2.id,
        text: h2.textContent || '',
      }));
    }
    return [];
  }

  @HostListener('window:resize')
  onResize() {
    if (this.isBrowser) {
      this.innerWidth = window.innerWidth;
      if (this.innerWidth > 990) {
        this.tableOfContentExpanded = true;
      }
    }
  }

  expandTableOfContent() {
    this.tableOfContentExpanded = !this.tableOfContentExpanded;
  }

  private addIDsToH2s(htmlString: any): string {
    try {
      const htmlContent =
        typeof htmlString === 'string'
          ? htmlString
          : this.sanitizer.sanitize(SecurityContext.HTML, htmlString) || '';

      const doc = new DOMParser().parseFromString(htmlContent, 'text/html');
      const h2s = doc.getElementsByTagName('h2');

      Array.from(h2s).forEach((h2, i) => {
        h2.id = `h2-${i}`;
      });

      return new XMLSerializer().serializeToString(doc);
    } catch (error) {
      return typeof htmlString === 'string' ? htmlString : '';
    }
  }

  public scrollTo(event: MouseEvent, elementId?: string): void {
    if (this.isBrowser && elementId) {
      event.preventDefault();
      const element: HTMLElement | null =
        this.document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  updateVideoUrl(id: string) {
    if (id) {
      this.dangerousVideoUrl = `https://www.youtube.com/embed/${id}`;
      this.youTubeVidLink = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.dangerousVideoUrl,
      );
      this.videoLoaded = true;
    } else {
      this.videoLoaded = false;
    }
  }

  injectYoutubeSchema(schema: any) {
    try {
      if (typeof schema === 'string') {
        this.youtubeSchema = JSON.parse(schema);
      } else if (typeof schema === 'object') {
        this.youtubeSchema = schema;
      }
    } catch (error) {
      this.youtubeSchema = null;
    }
  }

  capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  isCleaningCategory(): boolean {
    if (!this.howTo?.main_category_slug) return false;
    return this.cleaningCategories.includes(
      this.howTo.main_category_slug.toLowerCase(),
    );
  }
  goToUserHappyHelper(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.open(
        'https://happyhelper.dk?utm_medium=website_landingpage&utm_source=handyhand&utm_campaign=happy_handy_colab',
      );
    }
  }
  protected readonly environment = this.env;
}
